import * as React from "react";
import styled, { css } from "styled-components";
import ReactDOM from "react-dom";
import { ReactNode } from "react";

import {
  useEventListener,
  useOutsideItemClick
} from "src/library/hooks";

import { TextTruncate } from "../Text";
import { Icon } from "../Icon";
import { LoadingSpinner } from "../LoadingSpinner";
import { Icons } from "../Icon/types";

interface DropdownProps {
  title: string | ReactNode;
  DropdownInnerComponent?: React.ComponentType;
  isOpen: boolean;
  toggleOpen: (isOpen?: boolean) => void;
  isLoading?: boolean;
}

const Dropdown: React.ReactFCWithChildren<DropdownProps> = ({
  title, DropdownInnerComponent, isOpen, toggleOpen, isLoading, children
}) => {
  const popoutRef = React.useRef<HTMLDivElement>(null);
  const divRef = React.useRef<HTMLDivElement>(null);

  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  useOutsideItemClick([ divRef, popoutRef ], () => {
    toggleOpen(false);
  });

  React.useEffect(() => {
    if (isOpen) {
      setOverlayPosition();
    }
  }, [ isOpen ]);

  const setOverlayPosition = React.useCallback(() => {
    if (divRef.current && popoutRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;
      const inner = popoutRef.current.getBoundingClientRect();

      if (inner.width > window.outerWidth) {
        right = 0;
      } else if (inner.width > bounds.right) {
        right = 0;
      } else {
        right = bounds.right - inner.width;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });
    }
  }, [ divRef ]);

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, [ setOverlayPosition ]);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));

  return (
    <DropDownWrapper>
      <div
        ref={divRef}
        className={`dropdown-select ${isOpen && "active"}`}
        onClick={() => toggleOpen(!isOpen)}
      >
        {typeof title === "string" ? (
          <TextTruncate
            truncateProps={{ lines: 1 }}
            color="secondary"
            className="dropdown-title"
          >
            {title}
          </TextTruncate>
        ) : (
          <div className="dropdown-title">
            {title}
          </div>
        )}

        {isLoading ? (
          <LoadingSpinner
            className="loading-spinner"
            size={15}
            type="circular"
            position="inline"
          />
        ) : (
          <Icon
            $fillColor="lightGrey"
            icon={Icons.CHEVRONDOWN}
          />
        )}
      </div>

      {ReactDOM.createPortal(
        <DropDownInnerWrapper
          className={`dropdown-inner-wrapper ${isOpen ? "show" : "hidden"}`}
          ref={popoutRef}
          {...position}
        >
          {DropdownInnerComponent && <DropdownInnerComponent />}

          {children}
        </DropDownInnerWrapper>
        ,
        document.body)}

    </DropDownWrapper>
  );
};

const DropDownWrapper = styled.div`
  ${({ theme }) => css`
    .dropdown-select {
      width: 200px;
      background: ${theme.colors.white};
      border: 1px solid ${theme.colors.lightestGrey};
      border-radius: ${theme.borderRadius};
      position: relative;
      padding: 10px 10px;
      padding-right: 35px;
      transition: ${theme.transition.quicker};
      max-height: 40px;

      svg,
      .loading-spinner {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 5px;
      }
      &:hover,
      .active {
        cursor: pointer;
        border-color: ${theme.colors.primaryDark};

        svg {
          fill: ${theme.colors.primaryDark};
        }
      }
    }
  `}
`;

const DropDownInnerWrapper = styled.div<{ x: number; y: number }>`
  ${({
    theme, y, x
  }) => css`
    position: absolute;
    top: ${y}px;
    left: ${x}px;
    background: ${theme.colors.white};
    border: ${theme.colors.lightGrey};
    border-radius: ${theme.borderRadius};
    max-height: 500px;
    overflow-y: scroll;
    box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.25);
    z-index: 100;

    &.hidden {
      display: none;
    }
  `}
`;

export default Dropdown;
